<template>
  <div class="bank-account-card" @click="$emit('edit', bankAccount)">
    <div class="bank-account-card-header">
      <div class="bank-account-card-icon">
        <img v-if="showLogo" :src="bankAccount.urlLogo" @error="logoError = true" />
        <i v-else class="fa-solid fa-building-columns"></i>
      </div>
      <div class="bank-account-card-info">
        <span class="bank-account-card-name">{{ title }}</span>
        <span class="bank-account-card-description">{{ description }}</span>
      </div>
    </div>

    <div class="bank-account-card-balances">
      <span class="bank-account-card-balance">
        <span class="bank-account-card-balance-label">Saldo Inicial</span>
        <span v-if="hideBalance" class="bank-account-card-balance-hidden">••••••</span>
        <span v-else class="bank-account-card-balance-value">{{ openingBalance }}</span>
      </span>
      <span class="bank-account-card-balance">
        <span class="bank-account-card-balance-label">Saldo Atual</span>
        <span v-if="hideBalance" class="bank-account-card-balance-hidden">••••••</span>
        <span v-else class="bank-account-card-balance-value">{{ accountBalance }}</span>
      </span>
    </div>

    <div class="bank-account-card-actions">
      <div class="bank-account-card-buttons">
        <div v-b-tooltip.hover :title="hideBalance ? 'Mostrar Saldos' : 'Ocultar Saldos'" class="bank-account-card-btn"
          @click.stop="hideBalance = !hideBalance">
          <i :class="hideBalance ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
        </div>
        <div v-b-tooltip.hover title="Editar Conta" class="bank-account-card-btn"
          @click.stop="$emit('edit', bankAccount)">
          <i class="fa-solid fa-pen-to-square"></i>
        </div>
        <div v-b-tooltip.hover title="Excluir Conta" class="bank-account-card-btn bank-account-card-btn-danger"
          @click.stop="$emit('delete', bankAccount)">
          <i class="fa-solid fa-trash"></i>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "BankAccountCard",
  props: {
    bankAccount: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      hideBalance: true,
      logoError: false,
    };
  },
  computed: {
    title() {
      return this.bankAccount.name || this.bankAccount.bankName;
    },
    description() {
      let bankName = (this.bankAccount.bankName || "").trim();
      if (!bankName || bankName == this.bankAccount.name)
        return this.bankAccount.fullName;
      return `${bankName} · ${this.bankAccount.fullName}`;
    },
    showLogo() {
      return !!this.bankAccount.urlLogo && !this.bankAccount.urlLogo.endsWith("/") && !this.logoError;
    },
    openingBalance() {
      return this.formatCurrency(this.bankAccount.openingBalance);
    },
    accountBalance() {
      return this.formatCurrency(this.bankAccount.accountBalance);
    },
  },
  methods: {
    formatCurrency(value) {
      return Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },
  },
};
</script>

<style scoped>
.bank-account-card {
  background: var(--bg-base);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  width: 320px;
  padding: 16px 20px;
  cursor: pointer;
  transition: box-shadow 0.15s, border-color 0.15s, transform 0.15s;
}

.bank-account-card:hover {
  box-shadow: var(--shadow-sm);
  transform: translateY(-1px);
}

.bank-account-card-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.bank-account-card-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: var(--bg-elevated);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-brand);
  font-size: 16px;
  flex-shrink: 0;
  overflow: hidden;
}

.bank-account-card-icon >>> img {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  object-fit: cover;
}

.bank-account-card-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.bank-account-card-name {
  font-size: 15px;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
}

.bank-account-card-description {
  min-height: 18px;
  font-size: 12px;
  color: var(--text-muted);
  font-style: italic;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
}

.bank-account-card-balances {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.bank-account-card-balance {
  width: 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  padding: 3px 12px;
  border-radius: 999px;
  background: var(--bg-elevated);
  border: 1px solid var(--border-subtle);
  font-size: 12px;
}

.bank-account-card-balance-label {
  color: var(--text-secondary);
  white-space: nowrap;
}

.bank-account-card-balance-value {
  color: var(--text-primary);
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bank-account-card-balance-hidden {
  color: var(--text-muted);
  font-weight: 600;
  letter-spacing: 2px;
}

.bank-account-card-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-top: 12px;
  border-top: 1px solid var(--border-subtle);
}

.bank-account-card-buttons {
  display: flex;
  align-items: center;
  gap: 14px;
}

.bank-account-card-btn {
  font-size: 16px;
  cursor: pointer;
  color: var(--text-muted);
  transition: color 0.15s;
}

.bank-account-card-btn:hover {
  color: var(--color-brand);
}

.bank-account-card-btn-danger {
  color: var(--color-danger);
}
</style>
